import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import Logo from "./Logo";
import { useContent } from "@/context/ContentContext";
import { buildTelLink, scrollToId } from "@/lib/site";

const LINKS = [
  { id: "leistungen", label: "Leistungen" },
  { id: "preise", label: "Preise" },
  { id: "ablauf", label: "Ablauf" },
  { id: "faq", label: "FAQ" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const c = useContent();
  const tel = buildTelLink(c.settings);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const go = (id) => {
    setOpen(false);
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 140);
    } else {
      scrollToId(id);
    }
  };

  return (
    <header
      data-testid="nav-header"
      className="fixed top-0 inset-x-0 z-50 border-b border-white/5 bg-[#09090B]/70 backdrop-blur-xl"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between">
        <button data-testid="nav-logo" onClick={() => go("hero")} className="flex items-center gap-3">
          <Logo className="h-9" />
          <span className="font-display font-bold tracking-tight text-zinc-100 text-sm sm:text-base whitespace-nowrap">
            WIESE<span className="text-blue-400">·</span>FAHRZEUGPFLEGE
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {tel ? (
            <a
              data-testid="nav-phone-link"
              href={tel}
              className="hidden sm:flex h-10 w-10 items-center justify-center rounded-full border border-zinc-800 text-zinc-300 hover:border-blue-500/50 hover:text-blue-400 transition-colors"
              aria-label="Jetzt anrufen"
            >
              <Phone className="h-4 w-4" />
            </a>
          ) : null}
          <button
            data-testid="nav-cta-booking"
            onClick={() => go("termin")}
            className="hidden sm:inline-flex h-10 items-center rounded-full bg-blue-600 px-5 text-sm font-semibold text-white hover:bg-blue-500 transition-colors"
          >
            Termin anfragen
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="md:hidden flex h-10 w-10 items-center justify-center rounded-full border border-zinc-800 text-zinc-300"
            aria-label="Menü"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden overflow-hidden border-b border-white/5 bg-[#09090B]/95 backdrop-blur-xl"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="text-left py-3 text-base text-zinc-300 border-b border-white/5 last:border-0"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("termin")}
                className="mt-3 h-11 rounded-full bg-blue-600 text-sm font-semibold text-white"
              >
                Termin anfragen
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
